import { CommonModule } from '@angular/common';
import { Component, Input, LOCALE_ID, OnInit } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SharedModule } from '../../../@commons/ui-components/shared.module';
import { WeatherStatusResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/weather-status.response';
import { MoonStatusResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/location/moon-status.response';
import { AdviceTodayResponse } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/responses/advice/advice-today.response';
import { AdviceTrigger } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/advice-trigger.enum';
import { WeatherType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/weather-type.enum';
import { WeatherApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/location/weather/weather-api';
import { SkeletonLoaderComponent } from '../../../@commons/@components/loaders/skeleton-loader/skeleton-loader.component';
import { HexToHsl } from '../../../@commons/pipes/HexToHsl';
import { ExtendableCardComponent } from '../../../@commons/@components/cards/extendable-card/extendable-card.component';

interface ChipStyle {
  background: string;
  ink: string;
  emoji: string;
}

const CHIP_STYLES: Record<string, ChipStyle> = {
  [AdviceTrigger.FROST]: { background: '#E0F2FE', ink: '#075985', emoji: '❄️' },
  [AdviceTrigger.HEAVY_RAIN]: { background: '#E1EDFE', ink: '#1A4166', emoji: '🌧️' },
  [AdviceTrigger.HEATWAVE]: { background: '#FEF3C7', ink: '#92400E', emoji: '☀️' },
  [AdviceTrigger.DRY_SPELL]: { background: '#FEF9C3', ink: '#854D0E', emoji: '💧' },
  [AdviceTrigger.STRONG_WIND]: { background: '#F1F5F9', ink: '#475569', emoji: '💨' },
};

@Component({
  selector: 'vw-garden-today-card',
  imports: [
    CommonModule,
    SharedModule,
    TranslateModule,
    SkeletonLoaderComponent,
    HexToHsl,
    ExtendableCardComponent,
  ],
  templateUrl: './garden-today-card.component.html',
  styleUrl: './garden-today-card.component.scss',
  providers: [
    { provide: WeatherApi, useClass: WeatherApi },
    { provide: LOCALE_ID, useValue: 'fr' },
  ],
})
export class GardenTodayCardComponent implements OnInit {
  /** Fetched once by the home component, shared with the advices widget. */
  @Input() advice: AdviceTodayResponse | null = null;

  currentWeather: WeatherStatusResponse = null;
  moonData: MoonStatusResponse = null;

  iconUrl: string = null;
  backgroundColor = '#F5F5F5';
  weatherImg: string = '/assets/svg/weather/weather-tree.svg';
  currentDate: Date = new Date();
  isLoading: boolean = true;

  constructor(private weatherApi: WeatherApi) {}

  async ngOnInit() {
    await Promise.all([this.loadWeather(), this.loadMoon()]);
    this.setWeatherScreenData();
    this.isLoading = false;
  }

  private async loadWeather() {
    try {
      this.currentWeather = await this.weatherApi.getStatus();
    } catch (e) {
      console.warn('Failed to load weather data', e);
    }
  }

  private async loadMoon() {
    try {
      this.moonData = await this.weatherApi.getMoonStatus();
    } catch (e) {
      console.warn('Failed to load moon data', e);
    }
  }

  get hasContent(): boolean {
    return !!this.currentWeather || !!this.moonData;
  }

  /** Weather triggers only: the lunar day already has its own chip. */
  get weatherTriggers() {
    return (this.advice?.triggers ?? []).filter(
      (t) => !`${t.trigger}`.startsWith('LUNAR_'),
    );
  }

  /** User-address lunar day, computed server-side with the advice payload. */
  get lunar() {
    return this.advice?.lunar ?? null;
  }

  get moonPathIcon(): string {
    if (!this.lunar) return '';
    return this.lunar.moonPath === 'montante' ? '↑' : '↓';
  }

  chipStyle(trigger: string): ChipStyle {
    return CHIP_STYLES[trigger] ?? { background: '#F5F5F5', ink: '#3c4a45', emoji: '🌱' };
  }

  /** Same weather→visual mapping as the previous weather card. */
  private setWeatherScreenData() {
    if (!this.currentWeather) {
      return;
    }
    switch (this.currentWeather.currentWeather) {
      case WeatherType.SUN:
        if (this.currentWeather.currentTemperature > 25) {
          this.iconUrl = '/assets/svg/weather/sun-high-heat-colored.svg';
          this.backgroundColor = '#F49F37';
        } else if (this.currentWeather.currentTemperature > 20) {
          this.iconUrl = '/assets/svg/weather/sun-heat-colored.svg';
          this.backgroundColor = '#F4D537';
        } else {
          this.iconUrl = '/assets/svg/weather/sun-colored.svg';
          this.backgroundColor = '#FCFDDE';
        }
        break;
      case WeatherType.CLOUD:
        this.iconUrl = '/assets/svg/weather/cloud-colored.svg';
        this.backgroundColor = '#ECEBE7';
        break;
      case WeatherType.RAIN:
        if (this.currentWeather.totalPrecipitation > 15) {
          this.iconUrl = '/assets/svg/weather/cloud-rain-white.svg';
          this.backgroundColor = '#205280';
        } else if (this.currentWeather.totalPrecipitation > 5) {
          this.iconUrl = '/assets/svg/weather/cloud-rain-white.svg';
          this.backgroundColor = '#45739f';
        } else {
          this.iconUrl = '/assets/svg/weather/cloud-rain-colored.svg';
          this.backgroundColor = '#E1EDFE';
        }
        break;
      case WeatherType.THUNDER:
        this.iconUrl = '/assets/svg/weather/cloud-thunder-colored.svg';
        this.backgroundColor = '#C7CCD3';
        break;
      case WeatherType.WIND:
        this.iconUrl = '/assets/svg/weather/cloud-wind-colored.svg';
        this.backgroundColor = '#F6F7F8';
        break;
      case WeatherType.SNOW:
        this.iconUrl = '/assets/svg/weather/snow-colored.svg';
        this.backgroundColor = '#F6F7F8';
        break;
      case WeatherType.CLEAR:
      default:
        this.iconUrl = '/assets/svg/weather/cloudy-colored.svg';
        this.backgroundColor = '#F6F7E8';
        break;
    }
  }
}
